import { useMemo, useState } from "react";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { isEqual } from "lodash";
import { Flex, Text } from "styles/utility-components";
import { useLocation } from "wouter";

import { FormattedDate } from "components/app_layout/Typography";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem } from "components/ui/dropdown_menu";
import EmptyState from "components/ui/empty_state/EmptyState";
import toast from "components/ui/toast/Toast";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { Table, tableRowSelection } from "lib/core_components/Table/Table";
import { CustomerDocumentRequest_DocumentTypeEnum_5 } from "lib/generated/app-service-gql/graphql";
import {
  CustomerDocumentDetails,
  CustomerDocumentSortState,
  useCustomerDocuments,
} from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useDownloadCustomerDocument } from "lib/hooks/api/customer_documents/useDownloadCustomerDocument";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { SortState } from "lib/search/types";
import { CustomerDocumentSortBy, CustomerDocumentSortOrder } from "lib/types/graphQLEnums";
import { DeleteCustomerDocumentModal } from "./modals/DeleteCustomerDocumentModal";
import { RenameCustomerDocumentModal } from "./modals/RenameCustomerDocumentModal";
import ContentLibraryActionBar from "./ContentLibraryActionBar";
import ContentLibraryActionCards from "./ContentLibraryActionCards";
import { CustomerDocumentDrawer } from "./CustomerDocumentDrawer";
import UploadContentLibrary from "./UploadContentLibrary";

function isFilename(path: string): boolean {
  return /[^\\/]+\.[a-zA-Z0-9]+$/.test(path);
}

function getSortOrder(sort: SortState, key: string): "ascend" | "descend" | null {
  return sort.field !== key ? null : sort.order === "ASC" ? "ascend" : "descend";
}

type Props = {
  folderPath?: string;
  selectedRowGuids: string[];
  setSelectedRowGuids: React.Dispatch<React.SetStateAction<string[]>>;
};
export default function ContentLibraryTable({
  folderPath,
  selectedRowGuids,
  setSelectedRowGuids,
}: Props) {
  const [, setLocation] = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useTablePageSize("content-library", 10);
  const [displayDocument, setDisplayDocument] = useState<CustomerDocumentDetails | null>(null);

  const [sort, setSort] = useState<CustomerDocumentSortState>({
    field: CustomerDocumentSortBy.CreatedAt,
    order: CustomerDocumentSortOrder.Desc,
  });

  const {
    data: documents,
    totalCount,
    isError,
    isLoading,
  } = useCustomerDocuments(
    {
      documentType: CustomerDocumentRequest_DocumentTypeEnum_5.ContentLibrary,
      uiPath: folderPath,
      sortBy: sort.field,
      sortOrder: sort.order,
    },
    {
      page: currentPage,
      pageSize,
    },
  );

  const tableColumns: ColumnType<CustomerDocumentDetails>[] = useMemo(
    () => [
      {
        ...commonTableColumns.titleColumn,
        title: "Name",
        key: "fileName",
        sorter: true,
        sortOrder: getSortOrder(sort, "fileName"),
        sortDirections: ["descend", "ascend", "descend"],

        // No locations array because we're just opening the general doc here.
        // There's no pointing to specific sources involved
        onCell: (doc) => ({
          onClick: () => {
            // If a document has a storage path in s3, it means it's a file and we can display it.
            if (doc.isFolder) {
              // If it's a folder, we navigate to the nested folder path, otherwise it's just the first folder
              const nestedFolderPath = folderPath ? `${folderPath}/${doc.fileName}` : doc.fileName;
              setLocation(`/${nestedFolderPath}`);
            } else {
              setDisplayDocument(doc);
            }
          },
          style: { cursor: "pointer" },
        }),
        render: ({ fileName }: CustomerDocumentDetails) => (
          <Flex alignItems="center" gap={8}>
            <UIcon
              icon={isFilename(fileName) ? "file06" : "folder"}
              color="sysTextDefault"
              size={16}
            />
            <ClassNames>
              {({ css }) => (
                <EllipsisTooltipText
                  fullText={fileName}
                  containerClassname={css({
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    lineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    fontWeight: 600,
                  })}
                />
              )}
            </ClassNames>
          </Flex>
        ),
      },
      {
        ...commonTableColumns.dateColumn,
        title: "Date added",
        key: "createdAt",
        sorter: true,
        sortOrder: getSortOrder(sort, "createdAt"),
        sortDirections: ["descend", "ascend", "descend"],
        render: ({ createdAt }: CustomerDocumentDetails) => (
          <Text>{FormattedDate({ date: createdAt })}</Text>
        ),
      },
      {
        sizeConfig: commonTableColumns.actionColumn.sizeConfig,
        key: "actions",
        title: (
          <Flex alignItems="center" justifyContent="center">
            <UIcon icon="dotsVertical" color="sysTextDefault" />
          </Flex>
        ),
        render: (doc: CustomerDocumentDetails) => {
          return (
            <Flex alignItems="center" justifyContent="center">
              <ActionColumn document={doc} setSelectedRowGuids={setSelectedRowGuids} />
            </Flex>
          );
        },
      },
    ],
    [folderPath, setLocation, setSelectedRowGuids, sort],
  );

  return (
    <>
      {selectedRowGuids.length > 0 ? (
        <ContentLibraryActionBar
          selectedDocuments={documents.filter((doc) => selectedRowGuids.includes(doc.id))}
          onClearSelection={() => setSelectedRowGuids([])}
        />
      ) : (
        <>
          {/* Only show description if we're not in a folder */}
          {!folderPath && (
            <Description>
              Your content library is a centralised repository of past proposals, case studies, and
              relevant documents. It helps streamline the response process by providing easy access
              to historical data and insights.
            </Description>
          )}
          <ContentLibraryActionCards hasDocuments={documents.length > 0} uiPath={folderPath} />
        </>
      )}
      {!documents.length && !isLoading ? (
        <Flex flexDirection="column" aria-label="Content library empty state" alignItems="center">
          <UploadContentLibrary includeDescription={false} uiPath={folderPath} />
          <EmptyState
            type="noResults"
            heading="No files yet"
            description="Upload files or folders to populate your content library"
            size="large"
            width="100%"
          />
        </Flex>
      ) : (
        <Table<CustomerDocumentDetails>
          ariaLabel="Customer documents Table"
          isError={isError}
          loading={isLoading}
          rowKey="id"
          columns={tableColumns}
          dataSource={documents}
          onChange={(_, __, sorter) => {
            if (Array.isArray(sorter)) {
              sorter = sorter[0];
            }
            // If the sort order is not defined, we don't want to trigger a sort without an order
            if (!sorter.order) {
              return;
            }
            const newSortState: CustomerDocumentSortState = {
              order:
                sorter.order === "descend"
                  ? CustomerDocumentSortOrder.Desc
                  : CustomerDocumentSortOrder.Asc,
              field: sorter.columnKey as CustomerDocumentSortBy,
            };

            if (!isEqual(newSortState, sort)) {
              setSort(newSortState);
            }
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            onChange: (page, pageSize) => {
              setPageSize(pageSize);
              setCurrentPage(page);
            },
            showSizeChanger: true,
          }}
          rowSelection={tableRowSelection(selectedRowGuids, setSelectedRowGuids, documents, "id")}
        />
      )}
      <CustomerDocumentDrawer
        source={{
          documentId: displayDocument?.id ?? "",
          fileName: displayDocument?.fileName ?? "",
          locations: [],
        }}
        onClose={() => setDisplayDocument(null)}
        isOpen={!!displayDocument}
      />
    </>
  );
}

const Description = styled(Text)({
  maxWidth: 650,
});

type ActionColumnProps = {
  document: CustomerDocumentDetails;
  setSelectedRowGuids: React.Dispatch<React.SetStateAction<string[]>>;
};
function ActionColumn({ document, setSelectedRowGuids }: ActionColumnProps) {
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const { mutate: downloadDocument } = useDownloadCustomerDocument({
    onSuccess: async (_data, vars) => {
      toast.success(`Successfully downloaded document ${vars.fileName}`);
    },
    onError: (_error, vars) => {
      toast.error(`Failed to download document ${vars.fileName}`);
    },
  });

  return (
    <>
      <DropdownMenu
        trigger={
          <Button
            icon="dotsVertical"
            buttonType="tertiary"
            iconProps={{ color: "sysTextDefault" }}
          />
        }
        width="160px"
        align="end"
      >
        <DropdownMenuItem
          key="rename"
          icon="edit05"
          onClick={() => setRenameModalOpen(true)}
          aria-label="Rename"
        >
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem
          key="download"
          aria-label="Download"
          icon="download01"
          onClick={() => downloadDocument({ ...document })}
        >
          Download
        </DropdownMenuItem>
        <DropdownMenuItem
          key="delete"
          aria-label="Delete"
          icon="trash03"
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenu>

      <RenameCustomerDocumentModal
        docId={document.id}
        fileName={document.fileName}
        isOpen={isRenameModalOpen}
        onOpenChange={setRenameModalOpen}
      />
      <DeleteCustomerDocumentModal
        selectedDocuments={[document]}
        isOpen={isDeleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onSuccess={() => {
          setSelectedRowGuids((prev) => prev.filter((guid) => document.id !== guid));
        }}
      />
    </>
  );
}
